/* __next_internal_client_entry_do_not_use__ Default auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { tv } from 'tailwind-variants';
import { getImageProps } from 'next/image';
import { Image as JSSImage } from '@sitecore-content-sdk/nextjs';
import { withStandardComponentWrapper } from 'helpers/HOC';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { normalizeImageUrl } from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import { isValidNextImageDomain } from 'lib/next-config/plugins/images';
import { useIsMobile } from 'lib/hooks/useIsMobile';
import useIsEditing from 'lib/hooks/useIsEditing';
import { EditingHelpText } from 'helpers/Editing/EditingHelpText';
import useDictionary from 'lib/hooks/useDictionary';
import { getTestProps } from 'lib/testing/utils';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
const InlineImage = (props)=>{
    const { desktopImage, mobileImage, caption } = props?.fields || {};
    const isMobile = useIsMobile();
    const isEditing = useIsEditing();
    const { getDictionaryValue } = useDictionary();
    const { fallbackImage, caption: captionClass, imageContainer } = TAILWIND_VARIANTS();
    // Show placeholder in editing mode if no desktop image is provided
    // Desktop image is mandatory as it's the primary image and required for the component to render
    // Mobile image is optional and only used for responsive optimization on smaller screens
    if (isEditing && !desktopImage?.value?.src) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(EditingHelpText, {
                    priority: "warning",
                    children: getDictionaryValue('ImageUnavailable') || 'Select the entire InlineImage block to edit both desktop and mobile images.'
                }),
                /*#__PURE__*/ _jsxs("figure", {
                    "data-component": "authorable/shared/media/inline-image",
                    ...editingHydrationProps(isEditing),
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: imageContainer(),
                            children: /*#__PURE__*/ _jsx(JSSImage, {
                                editable: true,
                                field: desktopImage,
                                className: fallbackImage(),
                                alt: "No image selected"
                            })
                        }),
                        /*#__PURE__*/ _jsx("figcaption", {
                            className: captionClass(),
                            children: /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                field: caption
                            })
                        })
                    ]
                })
            ]
        });
    }
    // Don't render if no desktop image is provided
    if (!desktopImage?.value?.src) {
        return /*#__PURE__*/ _jsx(_Fragment, {});
    }
    // Use desktop image as fallback if mobile image is not provided
    const effectiveMobileImage = mobileImage?.value?.src ? mobileImage : desktopImage;
    // Don't render if effectiveMobileImage doesn't have valid value
    if (!effectiveMobileImage?.value?.src) {
        return /*#__PURE__*/ _jsx(_Fragment, {});
    }
    // Normalize image URLs and check domain validity
    const normalizedDesktopSrc = normalizeImageUrl(desktopImage.value.src);
    const normalizedMobileSrc = normalizeImageUrl(effectiveMobileImage.value.src);
    if (!normalizedDesktopSrc || !normalizedMobileSrc) {
        return /*#__PURE__*/ _jsx(_Fragment, {});
    }
    const isValidDomain = isValidNextImageDomain(normalizedDesktopSrc);
    // Helper function to safely convert string dimensions to numbers from sitecore
    const toDimension = (value, fallback)=>{
        const parsed = typeof value === 'number' ? value : parseInt(String(value || ''), 10);
        return isNaN(parsed) ? fallback : parsed;
    };
    // Common props for both images (alt not needed since we handle it separately)
    const common = {
        alt: '',
        unoptimized: !isValidDomain
    };
    // Get optimized image props for both desktop and mobile images
    const { props: { srcSet: desktopSrcSet, ...desktopRest } } = getImageProps({
        ...common,
        src: normalizedDesktopSrc,
        width: toDimension(desktopImage.value.width, 1280),
        height: toDimension(desktopImage.value.height, 516)
    });
    const { props: { srcSet: mobileSrcSet } } = getImageProps({
        ...common,
        src: normalizedMobileSrc,
        width: toDimension(effectiveMobileImage.value.width, 600),
        height: toDimension(effectiveMobileImage.value.height, 400)
    });
    // Determine which alt text to use based on screen size
    const altText = isMobile ? effectiveMobileImage.value.alt || '' : desktopImage.value.alt || '';
    return /*#__PURE__*/ _jsxs("figure", {
        "data-component": "authorable/shared/media/inline-image",
        ...editingHydrationProps(isEditing),
        ...getTestProps(`component-inline-image-${props?.rendering?.uid}`),
        children: [
            /*#__PURE__*/ _jsxs("picture", {
                children: [
                    /*#__PURE__*/ _jsx("source", {
                        media: "(min-width: 768px)",
                        srcSet: desktopSrcSet || normalizedDesktopSrc,
                        ...getTestProps(`desktop-image`)
                    }),
                    /*#__PURE__*/ _jsx("source", {
                        media: "(max-width: 767px)",
                        srcSet: mobileSrcSet || normalizedMobileSrc,
                        ...getTestProps(`mobile-image`)
                    }),
                    /*#__PURE__*/ _jsx("img", {
                        ...desktopRest,
                        src: normalizedDesktopSrc,
                        alt: altText,
                        className: fallbackImage(),
                        ...getTestProps(`fallback-image`)
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("figcaption", {
                className: captionClass(),
                children: /*#__PURE__*/ _jsx(PlainTextWrapper, {
                    field: caption,
                    ...getTestProps(`caption`)
                })
            })
        ]
    });
};
export const Default = withStandardComponentWrapper(InlineImage);
const TAILWIND_VARIANTS = tv({
    slots: {
        fallbackImage: [
            'max-w-full',
            'h-auto',
            'rounded-general-border-radius-image'
        ],
        caption: [
            'mt-2',
            'text-body-small',
            'text-color-text-text-secondary',
            'italic'
        ],
        imageContainer: [
            'flex',
            'flex-col',
            'items-center'
        ]
    }
});
